import React from 'react'
import { toEnglishNumbers } from './helpers'
import { SaudiRiyalIcon } from './icons'

const languageFormat = {
  ar: 'en-US',
  en: 'en-US',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL'
}

export const calculateMoney = (amount, user, formatted = true, array = false) => {
  let fixedAmount = amount

  if (fixedAmount && fixedAmount < 0.001 && fixedAmount > -0.001) {
    fixedAmount = 0
  }

  try {
    const options = {
      style: 'currency',
      currency: user?.company?.currency || 'USD',
      minimumFractionDigits: (fixedAmount * 100) % 100 === 0 ? 0 : 2
    }

    const formatFunc = () => {
      try {
        const formatter = new Intl.NumberFormat(languageFormat[user?.language] || 'en-US', options)
        return [formatter, formatter.formatToParts(fixedAmount)]
      } catch (e) {
        return [
          null,
          [
            {
              type: 'fraction',
              value: fixedAmount - Math.floor(fixedAmount) ? Number((fixedAmount - Math.floor(fixedAmount)).toFixed(2) * 100) : ''
            },
            { type: 'integer', value: Math.floor(fixedAmount) },
            { type: 'currency', value: user.company.currency }
          ]
        ]
      }
    }

    const [formatter, formattedAmount] = formatFunc()

    const isSaudi = user.company.currency === 'SAR'

    return formatted ? (
      <>
        <small>
          {user?.language === 'ar' || !isSaudi ? (
            <sup>
              &nbsp;
              {formattedAmount.find(f => f.type === (user?.language === 'ar' ? 'fraction' : 'currency'))?.value}
              &nbsp;
            </sup>
          ) : (
            <SaudiRiyalIcon />
          )}
        </small>
        {formattedAmount
          .filter(f => f.type === 'integer' || f.type === 'group' || f.type === 'minusSign')
          .map(f => f.value)
          .join('')}
        <small>
          {user?.language !== 'ar' || !isSaudi ? (
            <sup>
              &nbsp;{formattedAmount.find(f => f.type === (user?.language === 'ar' ? 'currency' : 'fraction'))?.value}&nbsp;
            </sup>
          ) : (
            <SaudiRiyalIcon />
          )}
        </small>
      </>
    ) : array ? (
      [
        formattedAmount.find(f => f.type === (user?.language === 'ar' ? 'fraction' : 'currency'))?.value,
        formattedAmount
          .filter(f => f.type === 'integer' || f.type === 'group' || f.type === 'minusSign')
          .map(f => f.value)
          .join(''),
        formattedAmount.find(f => f.type === (user?.language === 'ar' ? 'currency' : 'fraction'))?.value
      ]
    ) : (
      formatter?.format?.(fixedAmount) ?? fixedAmount
    )
  } catch (e) {
    return array ? ['', fixedAmount, ''] : fixedAmount
  }
}

export const formatAmount = amount => {
  let fixedAmount = amount

  if (fixedAmount && fixedAmount < 0.001 && fixedAmount > -0.001) {
    fixedAmount = 0
  }

  try {
    const options = {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits:
        (Number((fixedAmount || 0).toFixed(2)) * 100) % 100 === 0
          ? 0
          : (Number((fixedAmount || 0).toFixed(2)) * 100) % 10 === 0
          ? 1
          : 2
    }

    const formatFunc = () => {
      try {
        const formatter = new Intl.NumberFormat('en-US', options)
        return formatter.formatToParts(fixedAmount)
      } catch (e) {
        return [{ type: 'integer', value: Math.floor(fixedAmount) }]
      }
    }

    const formattedAmount = formatFunc()

    return formattedAmount
      .filter(f => ['integer', 'group', 'minusSign', 'fraction', 'decimal'].includes(f.type))
      .map(f => f.value)
      .join('')
  } catch (e) {
    return fixedAmount
  }
}

export const currencyHelper = () => {
  const formatter = value => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const parser = value => toEnglishNumbers(value.replace(/[,\s]/g, ''))
  return { formatter, parser }
}

const currencies = [
  { code: 'AED', currency: 'UAE Dirham' },
  { code: 'AFN', currency: 'Afghani' },
  { code: 'ALL', currency: 'Lek' },
  { code: 'AMD', currency: 'Armenian Dram' },
  { code: 'ANG', currency: 'Netherlands Antillean Guilder' },
  { code: 'AOA', currency: 'Kwanza' },
  { code: 'ARS', currency: 'Argentine Peso' },
  { code: 'AUD', currency: 'Australian Dollar' },
  { code: 'AWG', currency: 'Aruban Florin' },
  { code: 'AZN', currency: 'Azerbaijan Manat' },
  { code: 'BAM', currency: 'Convertible Mark' },
  { code: 'BBD', currency: 'Barbados Dollar' },
  { code: 'BDT', currency: 'Taka' },
  { code: 'BGN', currency: 'Bulgarian Lev' },
  { code: 'BHD', currency: 'Bahraini Dinar' },
  { code: 'BIF', currency: 'Burundi Franc' },
  { code: 'BMD', currency: 'Bermudian Dollar' },
  { code: 'BND', currency: 'Brunei Dollar' },
  { code: 'BOB', currency: 'Boliviano' },
  { code: 'BOV', currency: 'Mvdol' },
  { code: 'BRL', currency: 'Brazilian Real' },
  { code: 'BSD', currency: 'Bahamian Dollar' },
  { code: 'BTN', currency: 'Ngultrum' },
  { code: 'BWP', currency: 'Pula' },
  { code: 'BYN', currency: 'Belarusian Ruble' },
  { code: 'BZD', currency: 'Belize Dollar' },
  { code: 'CAD', currency: 'Canadian Dollar' },
  { code: 'CDF', currency: 'Congolese Franc' },
  { code: 'CHE', currency: 'WIR Euro' },
  { code: 'CHF', currency: 'Swiss Franc' },
  { code: 'CHW', currency: 'WIR Franc' },
  { code: 'CLF', currency: 'Unidad de Fomento' },
  { code: 'CLP', currency: 'Chilean Peso' },
  { code: 'CNY', currency: 'Yuan Renminbi' },
  { code: 'COP', currency: 'Colombian Peso' },
  { code: 'COU', currency: 'Unidad de Valor Real' },
  { code: 'CRC', currency: 'Costa Rican Colon' },
  { code: 'CUC', currency: 'Peso Convertible' },
  { code: 'CUP', currency: 'Cuban Peso' },
  { code: 'CVE', currency: 'Cabo Verde Escudo' },
  { code: 'CZK', currency: 'Czech Koruna' },
  { code: 'DJF', currency: 'Djibouti Franc' },
  { code: 'DKK', currency: 'Danish Krone' },
  { code: 'DOP', currency: 'Dominican Peso' },
  { code: 'DZD', currency: 'Algerian Dinar' },
  { code: 'EGP', currency: 'Egyptian Pound' },
  { code: 'ERN', currency: 'Nakfa' },
  { code: 'ETB', currency: 'Ethiopian Birr' },
  { code: 'EUR', currency: 'Euro' },
  { code: 'FJD', currency: 'Fiji Dollar' },
  { code: 'FKP', currency: 'Falkland Islands Pound' },
  { code: 'GBP', currency: 'Pound Sterling' },
  { code: 'GEL', currency: 'Lari' },
  { code: 'GHS', currency: 'Ghana Cedi' },
  { code: 'GIP', currency: 'Gibraltar Pound' },
  { code: 'GMD', currency: 'Dalasi' },
  { code: 'GNF', currency: 'Guinean Franc' },
  { code: 'GTQ', currency: 'Quetzal' },
  { code: 'GYD', currency: 'Guyana Dollar' },
  { code: 'HKD', currency: 'Hong Kong Dollar' },
  { code: 'HNL', currency: 'Lempira' },
  { code: 'HRK', currency: 'Kuna' },
  { code: 'HTG', currency: 'Gourde' },
  { code: 'HUF', currency: 'Forint' },
  { code: 'IDR', currency: 'Rupiah' },
  { code: 'ILS', currency: 'New Israeli Sheqel' },
  { code: 'INR', currency: 'Indian Rupee' },
  { code: 'IQD', currency: 'Iraqi Dinar' },
  { code: 'IRR', currency: 'Iranian Rial' },
  { code: 'ISK', currency: 'Iceland Krona' },
  { code: 'JMD', currency: 'Jamaican Dollar' },
  { code: 'JOD', currency: 'Jordanian Dinar' },
  { code: 'JPY', currency: 'Yen' },
  { code: 'KES', currency: 'Kenyan Shilling' },
  { code: 'KGS', currency: 'Som' },
  { code: 'KHR', currency: 'Riel' },
  { code: 'KMF', currency: 'Comorian Franc ' },
  { code: 'KPW', currency: 'North Korean Won' },
  { code: 'KRW', currency: 'Won' },
  { code: 'KWD', currency: 'Kuwaiti Dinar' },
  { code: 'KYD', currency: 'Cayman Islands Dollar' },
  { code: 'KZT', currency: 'Tenge' },
  { code: 'LAK', currency: 'Lao Kip' },
  { code: 'LBP', currency: 'Lebanese Pound' },
  { code: 'LKR', currency: 'Sri Lanka Rupee' },
  { code: 'LRD', currency: 'Liberian Dollar' },
  { code: 'LSL', currency: 'Loti' },
  { code: 'LYD', currency: 'Libyan Dinar' },
  { code: 'MAD', currency: 'Moroccan Dirham' },
  { code: 'MDL', currency: 'Moldovan Leu' },
  { code: 'MGA', currency: 'Malagasy Ariary' },
  { code: 'MKD', currency: 'Denar' },
  { code: 'MMK', currency: 'Kyat' },
  { code: 'MNT', currency: 'Tugrik' },
  { code: 'MOP', currency: 'Pataca' },
  { code: 'MRU', currency: 'Ouguiya' },
  { code: 'MUR', currency: 'Mauritius Rupee' },
  { code: 'MVR', currency: 'Rufiyaa' },
  { code: 'MWK', currency: 'Malawi Kwacha' },
  { code: 'MXN', currency: 'Mexican Peso' },
  { code: 'MXV', currency: 'Mexican Unidad de Inversion (UDI)' },
  { code: 'MYR', currency: 'Malaysian Ringgit' },
  { code: 'MZN', currency: 'Mozambique Metical' },
  { code: 'NAD', currency: 'Namibia Dollar' },
  { code: 'NGN', currency: 'Naira' },
  { code: 'NIO', currency: 'Cordoba Oro' },
  { code: 'NOK', currency: 'Norwegian Krone' },
  { code: 'NPR', currency: 'Nepalese Rupee' },
  { code: 'NZD', currency: 'New Zealand Dollar' },
  { code: 'OMR', currency: 'Rial Omani' },
  { code: 'PAB', currency: 'Balboa' },
  { code: 'PEN', currency: 'Sol' },
  { code: 'PGK', currency: 'Kina' },
  { code: 'PHP', currency: 'Philippine Piso' },
  { code: 'PKR', currency: 'Pakistan Rupee' },
  { code: 'PLN', currency: 'Zloty' },
  { code: 'PYG', currency: 'Guarani' },
  { code: 'QAR', currency: 'Qatari Rial' },
  { code: 'RON', currency: 'Romanian Leu' },
  { code: 'RSD', currency: 'Serbian Dinar' },
  { code: 'RUB', currency: 'Russian Ruble' },
  { code: 'RWF', currency: 'Rwanda Franc' },
  { code: 'SAR', currency: 'Saudi Riyal' },
  { code: 'SBD', currency: 'Solomon Islands Dollar' },
  { code: 'SCR', currency: 'Seychelles Rupee' },
  { code: 'SDG', currency: 'Sudanese Pound' },
  { code: 'SEK', currency: 'Swedish Krona' },
  { code: 'SGD', currency: 'Singapore Dollar' },
  { code: 'SHP', currency: 'Saint Helena Pound' },
  { code: 'SLL', currency: 'Leone' },
  { code: 'SOS', currency: 'Somali Shilling' },
  { code: 'SRD', currency: 'Surinam Dollar' },
  { code: 'SSP', currency: 'South Sudanese Pound' },
  { code: 'STN', currency: 'Dobra' },
  { code: 'SVC', currency: 'El Salvador Colon' },
  { code: 'SYP', currency: 'Syrian Pound' },
  { code: 'SZL', currency: 'Lilangeni' },
  { code: 'THB', currency: 'Baht' },
  { code: 'TJS', currency: 'Somoni' },
  { code: 'TMT', currency: 'Turkmenistan New Manat' },
  { code: 'TND', currency: 'Tunisian Dinar' },
  { code: 'TOP', currency: "Pa'anga" },
  { code: 'TRY', currency: 'Turkish Lira' },
  { code: 'TTD', currency: 'Trinidad and Tobago Dollar' },
  { code: 'TWD', currency: 'New Taiwan Dollar' },
  { code: 'TZS', currency: 'Tanzanian Shilling' },
  { code: 'UAH', currency: 'Hryvnia' },
  { code: 'UGX', currency: 'Uganda Shilling' },
  { code: 'USD', currency: 'US Dollar' },
  { code: 'UYI', currency: 'Uruguay Peso en Unidades Indexadas (URUIURUI)' },
  { code: 'UYU', currency: 'Peso Uruguayo' },
  { code: 'UZS', currency: 'Uzbekistan Sum' },
  { code: 'VEF', currency: 'Bolívar' },
  { code: 'VND', currency: 'Dong' },
  { code: 'VUV', currency: 'Vatu' },
  { code: 'WST', currency: 'Tala' },
  { code: 'XAF', currency: 'CFA Franc BEAC' },
  { code: 'XCD', currency: 'East Caribbean Dollar' },
  { code: 'XDR', currency: 'SDR (Special Drawing Right)' },
  { code: 'XOF', currency: 'CFA Franc BCEAO' },
  { code: 'XPF', currency: 'CFP Franc' },
  { code: 'XSU', currency: 'Sucre' },
  { code: 'XUA', currency: 'ADB Unit of Account' },
  { code: 'YER', currency: 'Yemeni Rial' },
  { code: 'ZAR', currency: 'Rand' },
  { code: 'ZMW', currency: 'Zambian Kwacha' },
  { code: 'ZWL', currency: 'Zimbabwe Dollar' }
]

export default currencies
